/* eslint-disable no-console */
import { useState, useEffect, useMemo, useCallback } from "react";
import { FaChevronLeft, FaChevronRight, FaDownload, FaTh, FaImage } from "react-icons/fa";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { USERS_ALL_VENTAS } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { getOportunidades, getEdificiosFull, getFotosBySaleId, getReportesBySaleIds, getClientes } from "../../utils";
import { Header } from "../Header";
import PdfReporteFotografico from "./PdfReporteFotografico";
import { pdf } from "@react-pdf/renderer";

const ArrowBtn = ({ direction, onClick }) => (
  <button
    onClick={onClick}
    style={{
      position: "absolute",
      top: "50%",
      [direction === "left" ? "left" : "right"]: 40,
      transform: "translateY(-50%)",
      zIndex: 2,
      background: "rgba(0,0,0,0.5)",
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      width: 40,
      height: 40,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    }}
  >
    {direction === "left" ? <FaChevronLeft /> : <FaChevronRight />}
  </button>
);

const ReporteFotograficoExterno = () => {
  const { pais, id: userId, rol, agencia, clienteId } = useAuthContext();

  const [ventas, setVentas] = useState([]);
  const [edificiosMap, setEdificiosMap] = useState({});
  const [selectedSaleId, setSelectedSaleId] = useState("");
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [ventasConReporte, setVentasConReporte] = useState([]);
  const [edificioImages, setEdificioImages] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [viewMode, setViewMode] = useState("slideshow"); // "slideshow" | "grid"

  // Cargar ventas y edificios
  useEffect(() => {
    if (!pais || pais === "Region") return;
    const currentYear = new Date().getFullYear();

    const loadData = async () => {
      try {
        const [salesData, edData, clientesData] = await Promise.all([
          getOportunidades({ pais, ano: currentYear }).catch(() => []),
          getEdificiosFull(),
          getClientes({ pais }).catch(() => []),
        ]);

        const allSales = salesData || [];

        // Mapa de clientes por ID para vincular agencia por ID
        const clientesMap = (clientesData || []).reduce((acc, c) => ({ ...acc, [c.id]: c }), {});

        let ventasFiltradas = allSales
          .filter((s) => s.estatus === "100%" && s.edificiosparticipantes && s.edificiosparticipantes.trim() !== "")
          .sort((a, b) => (b.id || 0) - (a.id || 0));

        // Usuarios de poder ven todo sin filtro
        if (USERS_ALL_VENTAS.includes(userId)) {
          // Sin filtro — ven todas las ventas
        } else if (rol === ROLES.AGENCIA) {
          // Filtrar por ID de agencia: cliente de la venta → su agencia debe coincidir con la del usuario
          ventasFiltradas = ventasFiltradas.filter((s) => {
            if (!s.client_id || !agencia) return false;
            const cliente = clientesMap[s.client_id];
            return cliente && Number(cliente.agencia) === Number(agencia);
          });
        } else if (rol === ROLES.CLIENTE) {
          // Filtrar por client_id: el ID del cliente de la venta debe coincidir con el cliente_id del usuario
          ventasFiltradas = ventasFiltradas.filter(
            (s) => s.client_id && clienteId && Number(s.client_id) === Number(clienteId)
          );
        }

        setVentas(ventasFiltradas);

        if (edData?.length > 0) {
          setEdificiosMap(edData.reduce((acc, e) => ({ ...acc, [e.id]: e }), {}));
        }
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [pais, rol, userId, agencia, clienteId]);

  // Cargar qué ventas ya tienen reportes
  useEffect(() => {
    if (ventas.length === 0) return;
    const loadReportes = async () => {
      try {
        const saleIds = ventas.map((v) => v.id);
        const data = await getReportesBySaleIds(saleIds);
        const idsConFotos = [...new Set(data.map((r) => r.sale_id))];
        setVentasConReporte(idsConFotos);
      } catch (error) {
        console.error("Error cargando reportes:", error);
      }
    };
    loadReportes();
  }, [ventas]);

  const ventasVisibles = useMemo(
    () => ventas.filter((v) => ventasConReporte.includes(v.id)),
    [ventas, ventasConReporte]
  );

  // Cargar fotos cuando se selecciona una venta
  useEffect(() => {
    if (!selectedSaleId) {
      setFotos([]);
      return;
    }
    const loadFotos = async () => {
      setLoadingFotos(true);
      try {
        const data = await getFotosBySaleId(Number(selectedSaleId));
        setFotos(data);
      } catch (error) {
        console.error("Error cargando fotos:", error);
      } finally {
        setLoadingFotos(false);
      }
    };
    loadFotos();
  }, [selectedSaleId]);

  const selectedSale = useMemo(() => ventas.find((v) => v.id === Number(selectedSaleId)), [ventas, selectedSaleId]);

  const edificiosVenta = useMemo(() => {
    if (!selectedSale?.edificiosparticipantes) return [];
    const ids = selectedSale.edificiosparticipantes
      .split(",")
      .map((s) => Number(s.trim()))
      .filter(Boolean);
    return ids
      .map((id) => edificiosMap[id])
      .filter(Boolean)
      .sort((a, b) => (a.edificio || "").localeCompare(b.edificio || ""));
  }, [selectedSale, edificiosMap]);

  // Solo edificios que tienen foto subida
  const slides = useMemo(() => {
    const result = [];
    fotos.forEach((foto) => {
      const edificio = edificiosMap[foto.idedificio];
      if (edificio) {
        result.push({ foto, edificio });
      }
    });
    return result;
  }, [fotos, edificiosMap]);

  // Cargar imágenes de edificios (foto exterior)
  const loadEdificioImage = useCallback(
    async (edificioId) => {
      if (edificioImages[edificioId] !== undefined) return;
      try {
        const src = await import(`../../img/edificios/${edificioId}.png`).catch(
          () => import(`../../img/edificios/${edificioId}.jpeg`)
        );
        setEdificioImages((prev) => ({ ...prev, [edificioId]: src.default }));
      } catch {
        setEdificioImages((prev) => ({ ...prev, [edificioId]: null }));
      }
    },
    [edificioImages]
  );

  useEffect(() => {
    slides.forEach((s) => loadEdificioImage(s.edificio.id));
  }, [slides, loadEdificioImage]);

  const handleDownloadPdf = async () => {
    if (!selectedSale || slides.length === 0) return;
    setPdfLoading(true);
    try {
      const doc = <PdfReporteFotografico slides={slides} selectedSale={selectedSale} edificioImages={edificioImages} />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Reporte-${selectedSale.cliente}${selectedSale.campana ? `-${selectedSale.campana}` : ""}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generando PDF:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const sliderSettings = {
    dots: false,
    infinite: slides.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    adaptiveHeight: false,
    beforeChange: (_, next) => setCurrentSlide(next),
  };

  const [sliderRef, setSliderRef] = useState(null);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "15px 15px 0", flexShrink: 0 }}>
        <Header
          centerContent={
            selectedSale && (
              <div className="d-flex align-items-center gap-4 flex-wrap" style={{ fontSize: "0.95rem" }}>
                <span>
                  <span className="fw-semibold">Cliente </span>
                  <span>{selectedSale.cliente}</span>
                </span>
                {selectedSale.campana && (
                  <span>
                    <span className="fw-semibold">Campa&ntilde;a </span>
                    <span>{selectedSale.campana}</span>
                  </span>
                )}
                <span>
                  <span className="fw-semibold">Edificios </span>
                  <span>{edificiosVenta.length}</span>
                </span>
                {selectedSale.periodopauta && (
                  <span>
                    <span className="fw-semibold">Per&iacute;odo </span>
                    <span>{selectedSale.periodopauta}d</span>
                  </span>
                )}
                {slides.length > 1 && (
                  <span>
                    <span className="fw-semibold">Foto </span>
                    <span>
                      {currentSlide + 1} de {slides.length}
                    </span>
                  </span>
                )}
              </div>
            )
          }
        >
          <div className="d-flex align-items-center gap-2">
            {slides.length > 0 && (
              <div className="btn-group btn-group-sm" role="group">
                <button
                  className={`btn ${viewMode === "slideshow" ? "btn-dark" : "btn-outline-dark"}`}
                  onClick={() => setViewMode("slideshow")}
                  title="Vista individual"
                >
                  <FaImage />
                </button>
                <button
                  className={`btn ${viewMode === "grid" ? "btn-dark" : "btn-outline-dark"}`}
                  onClick={() => setViewMode("grid")}
                  title="Vista grilla"
                >
                  <FaTh />
                </button>
              </div>
            )}
            {slides.length > 0 && (
              <button
                className="btn btn-outline-dark btn-sm"
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
                title="Descargar PDF"
              >
                {pdfLoading ? <span className="spinner-border spinner-border-sm" role="status" /> : <FaDownload />}
              </button>
            )}
            {ventasVisibles.length > 0 && (
              <select
                className="form-select form-select-sm"
                style={{ width: 340 }}
                value={selectedSaleId}
                onChange={(e) => setSelectedSaleId(e.target.value)}
              >
                <option value="">-- Seleccione un reporte --</option>
                {ventasVisibles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.cliente}
                    {v.campana ? ` — ${v.campana}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        </Header>
      </div>

      {/* Área principal */}
      <div style={{ flex: 1, minHeight: 0, background: "#f8f9fa" }}>
        {!selectedSaleId ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100">
            {ventasVisibles.length === 0 ? (
              <p className="text-muted fs-5">No hay reportes fotogr&aacute;ficos disponibles.</p>
            ) : (
              <p className="text-muted" style={{ fontSize: "1.1rem" }}>
                Seleccione un reporte para visualizar
              </p>
            )}
          </div>
        ) : loadingFotos ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="spinner-border text-primary me-2" role="status" />
            <span className="text-muted">Cargando reporte...</span>
          </div>
        ) : slides.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <span className="text-muted">A&uacute;n no hay fotos cargadas en este reporte.</span>
          </div>
        ) : viewMode === "grid" ? (
          <div style={{ height: "100%", overflow: "auto", padding: "16px 24px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 14,
              }}
            >
              {slides.map(({ foto, edificio }) => (
                <div
                  key={foto.id}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                >
                  <div style={{ padding: "8px 10px", lineHeight: 1.3 }}>
                    <div
                      className="fw-semibold"
                      style={{
                        fontSize: "0.75rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {edificio.edificio}
                    </div>
                    {edificio.direccion && (
                      <div
                        className="text-muted"
                        style={{
                          fontSize: "0.65rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {edificio.direccion}
                      </div>
                    )}
                  </div>
                  <img
                    src={foto.url}
                    alt={edificio.edificio}
                    style={{ width: "100%", height: 150, objectFit: "cover", cursor: "pointer" }}
                    onClick={() => window.open(foto.url, "_blank")}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ height: "100%", position: "relative" }}>
            <Slider ref={setSliderRef} {...sliderSettings}>
              {slides.map(({ foto, edificio }) => {
                const edImg = edificioImages[edificio.id];
                const totalPantallas = (edificio.pantallas || 0) + (edificio.totems || 0) + (edificio.espera || 0);

                return (
                  <div key={foto.id}>
                    <div
                      style={{
                        height: "calc(100vh - 52px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f8f9fa",
                        padding: "10px 60px 50px",
                      }}
                    >
                      {/* Bloque foto + ficha juntos, centrados */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 24,
                          alignItems: "stretch",
                          maxHeight: 720,
                        }}
                      >
                        {/* Foto principal */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={foto.url}
                            alt={edificio.edificio}
                            style={{
                              maxWidth: "100%",
                              maxHeight: 720,
                              objectFit: "contain",
                              borderRadius: 10,
                              border: "1px solid #dee2e6",
                            }}
                          />
                        </div>

                        {/* Ficha técnica - mismo alto que la foto */}
                        <div
                          style={{
                            width: 320,
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #dee2e6",
                              borderRadius: 12,
                              overflow: "hidden",
                              background: "#fff",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                            }}
                          >
                            {/* Barra verde con nombre */}
                            <div
                              style={{
                                background: "#2ce67c",
                                padding: "10px 10px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "1rem",
                                  fontWeight: 700,
                                  color: "#000",
                                  lineHeight: 1.2,
                                  fontFamily: "'Trebuchet MS', sans-serif",
                                }}
                              >
                                {edificio.edificio}
                              </div>
                            </div>

                            {/* Datos del edificio */}
                            <div
                              style={{
                                padding: "10px 10px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                              }}
                            >
                              {edificio.direccion && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    fontSize: "0.75rem",
                                    color: "#6c757d",
                                  }}
                                >
                                  {edificio.direccion}
                                  {edificio.ubicacion ? `, ${edificio.ubicacion}` : ""}
                                </div>
                              )}

                              {(edificio.personas || totalPantallas > 0) && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 28,
                                    padding: "10px 0",
                                    borderTop: "1px solid #eee",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                                  {edificio.personas && (
                                    <div style={{ textAlign: "center" }}>
                                      <div
                                        style={{
                                          fontSize: "1.6rem",
                                          fontWeight: 700,
                                          color: "#2ce67c",
                                          lineHeight: 1.1,
                                        }}
                                      >
                                        {Number(edificio.personas).toLocaleString("es-CL")}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.65rem",
                                          textTransform: "uppercase",
                                          color: "#999",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Personas
                                      </div>
                                    </div>
                                  )}
                                  {totalPantallas > 0 && (
                                    <div style={{ textAlign: "center" }}>
                                      <div
                                        style={{
                                          fontSize: "1.6rem",
                                          fontWeight: 700,
                                          color: "#2ce67c",
                                          lineHeight: 1.1,
                                        }}
                                      >
                                        {totalPantallas}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.65rem",
                                          textTransform: "uppercase",
                                          color: "#999",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Pantallas
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Imagen exterior del edificio */}
                            {edImg && (
                              <div style={{ flex: 1, minHeight: 120, overflow: "hidden" }}>
                                <img
                                  src={edImg}
                                  alt={edificio.edificio}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>

            {/* Flechas de navegación */}
            {slides.length > 1 && (
              <>
                <ArrowBtn direction="left" onClick={() => sliderRef?.slickPrev()} />
                <ArrowBtn direction="right" onClick={() => sliderRef?.slickNext()} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReporteFotograficoExterno;
